import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Globe, LogOut } from 'lucide-react';
import boxyLogo from '@/assets/boxy_logo.svg';
import { Button } from '@/components/ui/button';
import { LOCAL_AR, LOCAL_EN, ROUTE } from '@/config';
import { useAuthStore } from '@/stores/authStore';
import useLanguageStore, { type LanguageType } from '@/stores/useLanguageStore';
import { useUserStore } from '@/stores';
import InvitationsPanel from './InvitationsPanel';
import { useFetchMyInvitations } from '@/hooksApi';

/**
 * D14 — what somebody with NO workspace sees.
 *
 * 🔴 THE STATE THIS REPLACES WAS MEASURED, NOT IMAGINED. An invited person's
 * first login landed on `/settings` with the FULL dashboard chrome rendered —
 * New Order, Orders, Shipments, Finance, Claims, Ticket Center — for an account
 * that belongs to no workspace at all, with three company-scoped requests
 * (`profile/company`, `service-preferences`, `industries`) answering 403 behind
 * a half-broken profile form. The `/settings` landing is not a routing choice:
 * `Main` redirects there whenever `permissions.length === 0`, and a user with
 * no workspace has no roles, so every one of them arrives there.
 *
 * ⭐⭐⭐ THE FIX IS TO NOT RENDER `children`, NOT TO HIDE THE CHROME. Mounting
 * the page and then hiding its furniture still mounts the page, and the page is
 * what fires the company-scoped requests. Returning this INSTEAD means those
 * components never mount and the 403s cannot happen — the requests stop because
 * nothing asks, rather than because something filtered them afterwards.
 *
 * Reuses InvitationsPanel rather than restating it: accepting an invitation is
 * the same act here as in Settings, and a second implementation of it would
 * drift from the one that is already tested.
 */
const NoWorkspaceLanding = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchMyInvitations();
  const invitations = data?.object ?? [];

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearToken = useAuthStore((state: { clearToken?: () => void }) => state.clearToken);
  const setUser = useUserStore((state) => state.setUser);
  const { language, setLanguage } = useLanguageStore();

  /*
   * 🔴 THERE HAS TO BE A WAY OFF THIS SCREEN. The sidebar is not rendered here
   * — that is the point of the gate — and the sidebar is where sign-out lives,
   * so without this a person who declines their only invitation is stranded on
   * a dead page with no control at all, and clearing their session means
   * clearing the browser's storage by hand. Same reason the language toggle is
   * here: an Arabic speaker who lands in English otherwise has no way to change
   * it, because the header is not rendered either.
   */
  const handleSignOut = () => {
    clearToken?.();
    setUser(undefined);
    queryClient.removeQueries(['user']);
    navigate(ROUTE.LOGIN);
  };

  return (
    <div className='min-h-screen w-full bg-background'>
      <div className='mx-auto flex min-h-screen w-full max-w-[640px] flex-col items-center justify-center px-4 py-10'>
        {/*
          ⚠️ THE LOGO STAYS. The owner rejected an earlier workspace surface
          specifically for removing it — on a screen with no navigation at all
          it is the only thing telling the person whose product they are in.
        */}
        <img src={boxyLogo} alt='Boxy' className='mb-8 h-8' />

        {invitations.length > 0 ? (
          <>
            <h1 className='mb-1 text-center text-xl font-semibold text-sd-foreground'>
              {String(t('workspace.invitations_for_you'))}
            </h1>
            <p className='mb-6 text-center text-sm text-sd-muted-foreground'>
              {String(t('workspace.landing_invited_subtitle'))}
            </p>
            <div className='w-full'>
              <InvitationsPanel />
            </div>
          </>
        ) : (
          <div className='w-full rounded-2xl border border-sd-border bg-sd-card p-8 text-center shadow-sm'>
            <h1 className='mb-2 text-lg font-semibold text-sd-foreground'>
              {String(t('workspace.landing_empty_title'))}
            </h1>
            {/*
              Names the only action that can actually resolve this — the person
              cannot create a workspace (registration and creation are both
              Boxy-granted), so "contact support" would be advice they cannot
              act on either. Being invited again is the real route back.
            */}
            <p className='text-sm text-sd-muted-foreground'>
              {isLoading
                ? String(t('workspace.landing_checking'))
                : String(t('workspace.landing_empty_hint'))}
            </p>
          </div>
        )}

        <div className='mt-8 flex items-center gap-2'>
          <Button
            size='sm'
            variant='ghost'
            onClick={() =>
              setLanguage((language === LOCAL_EN ? LOCAL_AR : LOCAL_EN) as LanguageType)
            }
          >
            <Globe className='me-1 h-3.5 w-3.5' aria-hidden />
            {language === LOCAL_EN ? 'العربية' : 'English'}
          </Button>

          <Button size='sm' variant='ghost' onClick={handleSignOut}>
            <LogOut className='me-1 h-3.5 w-3.5' aria-hidden />
            {String(t('workspace.sign_out'))}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoWorkspaceLanding;
